import { useQuery } from '@apollo/client'
import qs from 'query-string'
import { useMemo } from 'react'
import NumberFormat from 'react-number-format'
import { useHistory, useLocation } from 'react-router'

import { Card, CryptoIcon, Spinner } from '../../components'
import { Pagination } from '../../components/pagination'
import { PoolsTable } from '../../components/pools-table'
import { SecurityIcon } from '../../components/settings-menu/icons'
import { Paths } from '../../config/routes'
import { getLoans } from './queries'

export function LoanApplications() {
    const { search } = useLocation()
    const queryParams = useMemo(() => qs.parse(search), [search])

    const page = useMemo(() => {
        return parseInt(queryParams['page']?.toString() || '0')
    }, [queryParams])

    const { data, loading } = useQuery(getLoans, {
        variables: { input: { limit: 10, offset: page } },
    })
    const history = useHistory()

    const items = useMemo(() => {
        if (loading) return []
        if (!data) return []
        return data.listLoanApplicationsFromNode.data
    }, [data, loading])

    const dataClasses = 'not-italic font-light text-sm leading-3 py-4'
    const enfasisClasses = ' text-black '
    return (
        <div className='grid h-full grid-cols-8 gap-16 pt-12'>
            <div className='col-span-8'>
                <Card className='w-full h-full py-8'>
                    <div className='flex flex-row justify-between'>
                        <h3>Loan applications</h3>
                        <Pagination
                            page={+page}
                            totalPages={10}
                            onChange={p =>
                                history.push(
                                    `${Paths.LOAN_APPLICATIONS}?page=${p}`,
                                )
                            }
                        />
                    </div>
                    {loading && (
                        <div className='flex items-center justify-center w-full h-32'>
                            <Spinner size='large' color='black' />
                        </div>
                    )}
                    {!loading && items.length === 0 && (
                        <div
                            onClick={() =>
                                history.push(`${Paths.LOAN_APPLICATIONS}`)
                            }
                            className='flex flex-col items-center justify-center h-32 gap-2 text-2xl font-semibold border-b border-solid cursor-pointer hover:bg-gray2 border-gray text-gray4 border-opacity-10 group'>
                            <SecurityIcon />
                            There are no loans on this page
                        </div>
                    )}
                    {items.length !== 0 && (
                        <PoolsTable
                            headers={[
                                'Asset',
                                'Loan Pool',
                                'Amount (Principle)',
                                'Amount(Interest)',
                                'Total',
                                'Term',
                                'Interest Rate',
                                'Monthly Repayment',
                                'Credit Score ',
                                'Status',
                            ]}
                            items={
                                <>
                                    {items.map((item: any) => {
                                        return (
                                            <tr
                                                onClick={() =>
                                                    history.push(
                                                        `/app/loan/${item.loan_application_id}/review`,
                                                    )
                                                }
                                                className='border-b border-solid cursor-pointer hover:bg-gray2 border-gray border-opacity-10 group'
                                                data-test-id={`transaction-item`}>
                                                <td
                                                    className={`${dataClasses} ${enfasisClasses}`}>
                                                    <div className='flex flex-row justify-center w-full p-4'>
                                                        <CryptoIcon name='USDC' />
                                                    </div>
                                                </td>
                                                <td
                                                    className={`${dataClasses} ${enfasisClasses}`}>
                                                    <div className='flex flex-col items-center overflow-visible text-left break-words truncate'>
                                                        <p className='font-light text-black'>
                                                            Masa USDC A Pool
                                                        </p>
                                                    </div>
                                                </td>
                                                <td
                                                    className={`${dataClasses} ${enfasisClasses}`}>
                                                    <div className='flex flex-col items-center overflow-visible text-left break-words truncate'>
                                                        <p className='font-normal text-black'>
                                                            $
                                                            <NumberFormat
                                                                value={
                                                                    item.data
                                                                        .amount
                                                                }
                                                                type='text'
                                                                thousandSeparator={
                                                                    true
                                                                }
                                                                fixedDecimalScale={
                                                                    true
                                                                }
                                                                decimalScale={2}
                                                                displayType={
                                                                    'text'
                                                                }
                                                                className='inline'
                                                            />
                                                        </p>
                                                    </div>
                                                </td>
                                                <td
                                                    className={`${dataClasses} ${enfasisClasses}`}>
                                                    <div className='flex flex-col items-center overflow-visible text-left break-words truncate'>
                                                        <p className='font-normal text-black'>
                                                            $
                                                            <NumberFormat
                                                                value={
                                                                    item.data
                                                                        .amount *
                                                                    0.15
                                                                }
                                                                type='text'
                                                                thousandSeparator={
                                                                    true
                                                                }
                                                                fixedDecimalScale={
                                                                    true
                                                                }
                                                                decimalScale={2}
                                                                displayType={
                                                                    'text'
                                                                }
                                                                className='inline'
                                                            />
                                                        </p>
                                                    </div>
                                                </td>
                                                <td
                                                    className={`${dataClasses} ${enfasisClasses}`}>
                                                    <div className='flex flex-col items-center overflow-visible text-left break-words truncate'>
                                                        <p className='font-normal text-black'>
                                                            $
                                                            <NumberFormat
                                                                value={
                                                                    item.data
                                                                        .amount *
                                                                    1.15
                                                                }
                                                                type='text'
                                                                thousandSeparator={
                                                                    true
                                                                }
                                                                fixedDecimalScale={
                                                                    true
                                                                }
                                                                decimalScale={2}
                                                                displayType={
                                                                    'text'
                                                                }
                                                                className='inline'
                                                            />
                                                        </p>
                                                    </div>
                                                </td>
                                                <td
                                                    className={`${dataClasses} ${enfasisClasses}`}>
                                                    <div className='flex flex-col items-center overflow-visible text-left break-words truncate'>
                                                        <p className='font-light text-gray4'>
                                                            {item.data.term}{' '}
                                                            months
                                                        </p>
                                                    </div>
                                                </td>
                                                <td
                                                    className={`${dataClasses} ${enfasisClasses}`}>
                                                    <div className='flex flex-col items-center overflow-visible text-left break-words truncate'>
                                                        <p className='font-light text-black'>
                                                            $
                                                            {item.data
                                                                .interest_rate *
                                                                100}
                                                            % APY
                                                        </p>
                                                    </div>
                                                </td>
                                                <td
                                                    className={`${dataClasses} ${enfasisClasses}`}>
                                                    <div className='flex flex-col items-center overflow-visible text-left break-words truncate'>
                                                        <p className='font-light text-black'>
                                                            $447.92
                                                        </p>
                                                    </div>
                                                </td>
                                                <td
                                                    className={`${dataClasses} ${enfasisClasses}`}>
                                                    <div className='flex flex-col items-center overflow-visible text-left break-words truncate'>
                                                        <p className='font-semibold text-black'>
                                                            {/* A -{' '}
                                                        {Math.floor(
                                                            Math.random() * 900,
                                                        )} */}
                                                            Coming soon
                                                        </p>
                                                    </div>
                                                </td>
                                                <td
                                                    className={`${dataClasses} ${enfasisClasses}`}>
                                                    <div className='flex flex-col items-center overflow-visible text-left break-words truncate'>
                                                        <p className='font-semibold text-black'>
                                                            Pending{' '}
                                                            {Math.floor(
                                                                Math.random() *
                                                                    9,
                                                            )}
                                                            /10
                                                        </p>
                                                    </div>
                                                </td>
                                            </tr>
                                        )
                                    })}
                                </>
                            }
                        />
                    )}
                    {items.length > 4 && (
                        <Pagination
                            page={+page}
                            totalPages={10}
                            onChange={p =>
                                history.push(
                                    `${Paths.LOAN_APPLICATIONS}?page=${p}`,
                                )
                            }
                        />
                    )}
                </Card>
            </div>
        </div>
    )
}
